import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserPlus, Trash2, Shield, User as UserIcon, Loader2, Palette, Tag, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const empty = { name: "", email: "", password: "", role: "user" };

export default function Admin() {
  const { user: me, appName, updateAppName } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [brand, setBrand] = useState("");
  const [savingBrand, setSavingBrand] = useState(false);
  const [categories, setCategories] = useState([]);
  const [catName, setCatName] = useState("");
  const [catColor, setCatColor] = useState("#3b82f6");
  const [savingCat, setSavingCat] = useState(false);
  const [catToDelete, setCatToDelete] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  const loadCats = () => api.get("/categories").then((r) => setCategories(r.data));
  useEffect(() => { load(); loadCats(); }, []);
  useEffect(() => { setBrand(appName || ""); }, [appName]);

  const createCat = async () => {
    if (!catName.trim()) return toast.error("Informe o nome da categoria");
    setSavingCat(true);
    try {
      await api.post("/categories", { name: catName.trim(), color: catColor });
      toast.success("Categoria criada");
      setCatName("");
      setCatColor("#3b82f6");
      loadCats();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingCat(false);
    }
  };

  const removeCat = async () => {
    try {
      await api.delete(`/categories/${catToDelete.id}`);
      toast.success("Categoria removida");
      loadCats();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setCatToDelete(null);
  };

  const saveBrand = async () => {
    setSavingBrand(true);
    try {
      await updateAppName(brand);
      toast.success("Nome do app atualizado");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSavingBrand(false);
    }
  };

  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Preencha todos os campos");
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Usuário criado");
      setOpen(false);
      setForm(empty);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (id, role) => {
    await api.put(`/users/${id}`, { role });
    toast.success("Cargo atualizado");
    load();
  };

  const remove = async () => {
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success("Usuário removido");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setToDelete(null);
  };

  return (
    <div className="fade-up space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-head text-4xl font-extrabold tracking-tight">Administração</h1>
          <p className="text-muted-foreground mt-1">Gerencie usuários, cargos e permissões</p>
        </div>
        <Button data-testid="add-user-button" className="font-semibold gap-2" onClick={() => { setForm(empty); setOpen(true); }}>
          <UserPlus className="h-4 w-4" /> Novo usuário
        </Button>
      </div>

      <Card className="p-6 border-border/60 shadow-none rounded-md" data-testid="branding-card">
        <div className="flex items-center gap-2 mb-1">
          <Palette className="h-4 w-4 text-primary" aria-hidden />
          <h2 className="font-head text-lg font-bold tracking-tight">Identidade do app</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Nome exibido no topo, no menu lateral e na tela de login.</p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end max-w-md">
          <div className="flex-1 space-y-2">
            <Label>Nome do app</Label>
            <Input data-testid="app-name-input" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Ex: Minha Empresa" />
          </div>
          <Button onClick={saveBrand} disabled={savingBrand} data-testid="save-app-name-button" className="font-semibold">
            {savingBrand ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar"}
          </Button>
        </div>
      </Card>

      <Card className="p-6 border-border/60 shadow-none rounded-md" data-testid="categories-card">
        <div className="flex items-center gap-2 mb-1">
          <Tag className="h-4 w-4 text-primary" aria-hidden />
          <h2 className="font-head text-lg font-bold tracking-tight">Categorias de despesas</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Usadas na despesa e nos contratos, e no gráfico do Dashboard.</p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end mb-5">
          <div className="flex-1 space-y-2">
            <Label>Nome da categoria</Label>
            <Input data-testid="category-name-input" value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="Ex: Streamer" />
          </div>
          <div className="space-y-2">
            <Label>Cor</Label>
            <input type="color" value={catColor} onChange={(e) => setCatColor(e.target.value)} data-testid="category-color-input" className="h-10 w-14 rounded-md border border-border cursor-pointer bg-transparent p-1" />
          </div>
          <Button onClick={createCat} disabled={savingCat} data-testid="add-category-button" className="font-semibold gap-2">
            {savingCat ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /> Adicionar</>}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="categories-list">
          {categories.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma categoria cadastrada.</p>
          ) : categories.map((c) => (
            <span key={c.id} data-testid={`category-chip-${c.id}`} className="inline-flex items-center gap-2 pl-2.5 pr-1.5 py-1.5 rounded-full border border-border/60 text-sm">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />
              {c.name}
              <button onClick={() => setCatToDelete(c)} data-testid={`delete-category-${c.id}`} className="text-muted-foreground hover:text-destructive ml-0.5">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      </Card>

      <Card className="border-border/60 shadow-none rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuário</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Cargo</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold">{u.name?.[0]?.toUpperCase() || "?"}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium">{u.name}{u.id === me.id && <span className="text-xs text-muted-foreground ml-1">(você)</span>}</span>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)} disabled={u.id === me.id}>
                    <SelectTrigger className="w-36" data-testid={`role-select-${u.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin"><span className="flex items-center gap-2"><Shield className="h-3.5 w-3.5" /> Admin</span></SelectItem>
                      <SelectItem value="user"><span className="flex items-center gap-2"><UserIcon className="h-3.5 w-3.5" /> Usuário</span></SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" disabled={u.id === me.id} data-testid={`delete-user-${u.id}`} onClick={() => setToDelete(u)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl tracking-tight">Novo usuário</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Nome</Label><Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-2"><Label>E-mail</Label><Input type="email" data-testid="user-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-2"><Label>Senha</Label><Input type="password" data-testid="user-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div className="space-y-2">
              <Label>Cargo</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Usuário</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={create} disabled={saving} data-testid="user-save-button" className="font-semibold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover usuário?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.name}" perderá o acesso ao sistema.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-user">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!catToDelete} onOpenChange={(o) => !o && setCatToDelete(null)}>
        <AlertDialogContent data-testid="delete-category-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover categoria?</AlertDialogTitle>
            <AlertDialogDescription>"{catToDelete?.name}" será removida. As despesas ligadas ficarão sem categoria.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={removeCat} className="bg-destructive hover:bg-destructive/90" data-testid="confirm-delete-category">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
